import React from 'react';
import { companyInfo, serviceAreas } from '../data/mock';
import { serviceSEO } from '../data/seoData';
import { serviceDescriptions, serviceFAQs, serviceFeatures } from '../data/serviceData';

/**
 * ServiceSchema - Comprehensive schema markup for individual service pages
 * Optimized for LLMs, AI assistants, GEO, AEO, and Local SEO
 */
const ServiceSchema = ({ serviceId, serviceName }) => {
  const seo = serviceSEO[serviceId] || {};
  const description = serviceDescriptions[serviceId] || '';
  const faqs = serviceFAQs[serviceId] || [];
  const features = serviceFeatures[serviceId] || [];

  // Service Schema - Primary service structured data
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `https://graycontractingdfw.com/services/${serviceId}#service`,
    "name": serviceName,
    "description": description,
    "provider": {
      "@type": "HomeAndConstructionBusiness",
      "@id": "https://graycontractingdfw.com/#organization",
      "name": companyInfo.name,
      "telephone": companyInfo.phone,
      "priceRange": "$$"
    },
    "serviceType": serviceName,
    "areaServed": serviceAreas.map(area => ({
      "@type": "City",
      "name": area,
      "containedInPlace": {
        "@type": "State",
        "name": "Texas",
        "containedInPlace": {
          "@type": "Country",
          "name": "United States"
        }
      }
    })),
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": `${serviceName} Services`,
      "itemListElement": features.map((feature, index) => ({
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": feature
        },
        "position": index + 1
      }))
    },
    "termsOfService": "Free estimates provided. Licensed and insured.",
    "serviceOutput": {
      "@type": "Thing",
      "name": `Completed ${serviceName}`
    }
  };

  // FAQ Schema for the specific service
  const faqSchema = faqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a
      }
    }))
  } : null;

  // BreadcrumbList Schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://graycontractingdfw.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Services",
        "item": "https://graycontractingdfw.com/services"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": serviceName,
        "item": `https://graycontractingdfw.com/services/${serviceId}`
      }
    ]
  };

  // LocalBusiness with specific service
  const localServiceSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `https://graycontractingdfw.com/services/${serviceId}#localbusiness`,
    "name": `${companyInfo.name} - ${serviceName}`,
    "description": seo.description || description,
    "url": `https://graycontractingdfw.com/services/${serviceId}`,
    "telephone": companyInfo.phone,
    "email": companyInfo.email,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": companyInfo.addressStreet,
      "addressLocality": companyInfo.addressCity,
      "addressRegion": companyInfo.addressState,
      "postalCode": companyInfo.addressZip,
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "32.8378",
      "longitude": "-96.7691"
    },
    "priceRange": "$$",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "127",
      "bestRating": "5"
    },
    "makesOffer": {
      "@type": "Offer",
      "itemOffered": {
        "@type": "Service",
        "name": serviceName,
        "description": description
      }
    }
  };

  // WebPage Schema for AEO/GEO optimization
  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `https://graycontractingdfw.com/services/${serviceId}#webpage`,
    "url": `https://graycontractingdfw.com/services/${serviceId}`,
    "name": seo.title || serviceName,
    "description": seo.description || description,
    "isPartOf": {
      "@type": "WebSite",
      "@id": "https://graycontractingdfw.com/#website",
      "name": companyInfo.name,
      "url": "https://graycontractingdfw.com"
    },
    "about": {
      "@type": "Service",
      "@id": `https://graycontractingdfw.com/services/${serviceId}#service`
    },
    "mainEntity": {
      "@type": "Service",
      "@id": `https://graycontractingdfw.com/services/${serviceId}#service`
    },
    "speakable": {
      "@type": "SpeakableSpecification",
      "cssSelector": ["h1", ".service-description", ".service-features"]
    },
    "potentialAction": {
      "@type": "ReadAction",
      "target": `https://graycontractingdfw.com/services/${serviceId}`
    }
  };

  // HowTo Schema (for service process)
  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": `How to Get ${serviceName} in Dallas`,
    "description": `Step-by-step process for ${serviceName.toLowerCase()} services from Gray Contracting in Dallas-Fort Worth.`,
    "step": [
      {
        "@type": "HowToStep",
        "position": 1,
        "name": "Request Free Consultation",
        "text": "Contact Gray Contracting at 214-908-7644 or fill out our online form for a free consultation.",
        "url": "https://graycontractingdfw.com/contact"
      },
      {
        "@type": "HowToStep",
        "position": 2,
        "name": "Receive Detailed Quote",
        "text": "We assess your needs and provide transparent pricing with no hidden fees."
      },
      {
        "@type": "HowToStep",
        "position": 3,
        "name": "Schedule Service",
        "text": "Choose a convenient time for our licensed professionals to complete the work."
      },
      {
        "@type": "HowToStep",
        "position": 4,
        "name": "Quality Workmanship",
        "text": "Our skilled craftsmen deliver expert results backed by 55+ years of experience."
      },
      {
        "@type": "HowToStep",
        "position": 5,
        "name": "Final Walkthrough",
        "text": "We ensure 100% satisfaction with a thorough final inspection."
      }
    ],
    "totalTime": "P1D",
    "estimatedCost": {
      "@type": "MonetaryAmount",
      "currency": "USD",
      "value": "Varies by project scope"
    }
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />
      {faqSchema && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(localServiceSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(howToSchema) }} />
    </>
  );
};

export default ServiceSchema;
